import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { setCommitteeContext } from '@/utils/committeeContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Award, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SignupPage = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [searchParams] = useSearchParams();
  const committeeSlug = searchParams.get('committee');
  const [committeeInfo, setCommitteeInfo] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    affiliation: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchCommitteeInfo = async () => {
    if (!committeeSlug) return null;
    try {
      const response = await axios.get(`${API}/public/committees/${committeeSlug}`);
      const committee = response.data?.committee;
      if (committee) {
        setCommitteeContext(committee);
        setCommitteeInfo(committee);
      }
      return committee || null;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    fetchCommitteeInfo();
  }, [committeeSlug]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await signup(formData.email, formData.password, formData.firstName, formData.lastName, 'applicant', { phone: formData.phone, affiliation: formData.affiliation });
      toast.success('Account created successfully!');
      let committeeContext = committeeInfo;
      if (committeeSlug && !committeeContext) {
        committeeContext = await fetchCommitteeInfo();
      }
      if (committeeContext?.slug) {
        navigate(`/${committeeContext.slug}/dashboard`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 bg-greek-blue relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-greek-blue to-greek-blue-dark"></div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <div className="w-24 h-24 bg-greek-gold rounded-sm flex items-center justify-center mx-auto mb-8">
              <Award className="w-14 h-14 text-white" />
            </div>
            <h2 className="font-heading text-4xl font-bold mb-4">Join Our Legacy</h2>
            <p className="text-white/80 text-lg max-w-md mx-auto">
              Apply for the AHEPA Family Scholarship and take the next step in your academic journey.
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-greek-gold"></div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link
            to={committeeSlug ? `/${committeeSlug}` : '/'}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-greek-blue mb-8 transition-colors"
            data-testid="signup-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-greek-blue rounded-sm flex items-center justify-center">
                <Award className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold text-greek-blue">AHEPA Family</h1>
                <p className="text-xs text-slate-500">Scholarship Program</p>
              </div>
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-900">Create Account</h2>
            <p className="text-slate-600 mt-2">Start your scholarship application today</p>
            {committeeInfo?.name && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700" data-testid="signup-committee-context">
                Creating account for {committeeInfo.name}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-slate-700">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="h-12 rounded-sm border-slate-300 focus:border-greek-blue focus:ring-greek-blue"
                  data-testid="signup-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-slate-700">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder="Papadopoulos"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="h-12 rounded-sm border-slate-300 focus:border-greek-blue focus:ring-greek-blue"
                  data-testid="signup-lastname-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-12 rounded-sm border-slate-300 focus:border-greek-blue focus:ring-greek-blue"
                data-testid="signup-email-input"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-700">Telephone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={handleChange}
                  className="h-12 rounded-sm border-slate-300 focus:border-greek-blue focus:ring-greek-blue"
                  data-testid="signup-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="affiliation" className="text-slate-700">Organization Affiliation</Label>
                <Select
                  value={formData.affiliation}
                  onValueChange={(value) => setFormData({ ...formData, affiliation: value })}
                >
                  <SelectTrigger className="h-12 bg-white rounded-sm border-slate-300 focus:border-greek-blue focus:ring-greek-blue" data-testid="signup-affiliation-select">
                    <SelectValue placeholder="Select Organization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AHEPA">AHEPA</SelectItem>
                    <SelectItem value="DOP">DOP</SelectItem>
                    <SelectItem value="MOA">MOA</SelectItem>
                    <SelectItem value="SOP">SOP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Minimum 6 characters"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="h-12 rounded-sm border-slate-300 focus:border-greek-blue focus:ring-greek-blue pr-12"
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="h-12 rounded-sm border-slate-300 focus:border-greek-blue focus:ring-greek-blue"
                data-testid="signup-confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm font-semibold tracking-wide uppercase"
              data-testid="signup-submit-btn"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <p className="text-center mt-8 text-slate-600">
            Already have an account?{' '}
            <Link
              to={committeeSlug ? `/login?committee=${committeeSlug}` : '/login'}
              className="text-greek-blue font-semibold hover:underline"
              data-testid="signup-login-link"
            >
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
